import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaClock, 
  FaUser, 
  FaMapMarkerAlt, 
  FaRubleSign,
  FaChartLine,
  FaEye,
  FaDownload,
  FaSync,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationCircle,
  FaCheckCircle,
  FaTimes
} from 'react-icons/fa';
import { adminApiService } from '../services/adminApi';

const CashShifts = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // Фильтры
  const [filters, setFilters] = useState({
    establishment_id: '',
    status: 'closed',
    date_from: '',
    date_to: '',
    cashier_id: '',
    page: 1,
    limit: 20
  });
  
  // Данные для фильтров
  const [establishments, setEstablishments] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [summaryStats, setSummaryStats] = useState(null);
  const [pagination, setPagination] = useState(null);

  // Создаем API клиент для кассовых смен с правильной обработкой ошибок
  const cashierAPI = {
    get: async (url) => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          throw new Error('Токен авторизации не найден');
        }

        // Используем относительные пути для работы с proxy
        const fullURL = url;
        
        console.log('Запрос к API:', fullURL);
        
        const response = await fetch(fullURL, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        console.log('Статус ответа:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Ошибка API:', response.status, errorText);
          throw new Error(`HTTP ${response.status}: ${errorText || 'Ошибка сервера'}`);
        }
        
        const data = await response.json();
        console.log('Данные получены:', data);
        return { data };
      } catch (error) {
        console.error('Ошибка запроса:', error);
        throw error;
      }
    }
  };

  const loadShifts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      
      console.log('Загрузка смен с параметрами:', params.toString());
      
      const response = await cashierAPI.get(`/api/cashier/admin/shifts/?${params}`);
      
      // Проверяем структуру ответа
      if (response.data && typeof response.data === 'object') {
        setShifts(response.data.shifts || []);
        setSummaryStats(response.data.summary_stats || null);
        setPagination(response.data.pagination || null);
      } else {
        console.warn('Неожиданная структура ответа:', response.data);
        setShifts([]);
        setSummaryStats(null);
        setPagination(null);
      }
      
    } catch (err) {
      console.error('Ошибка загрузки смен:', err);
      setError(`Ошибка загрузки смен: ${err.message}`);
      setShifts([]);
      setSummaryStats(null);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadEstablishments = useCallback(async () => {
    try {
      console.log('Загрузка заведений...');
      const response = await adminApiService.getEstablishments();
      console.log('Заведения загружены:', response);
      setEstablishments(Array.isArray(response) ? response : response.results || []);
    } catch (err) {
      console.error('Ошибка загрузки заведений:', err);
      setEstablishments([]);
    }
  }, []);

  const loadCashiers = useCallback(async () => {
    if (!filters.establishment_id) {
      setCashiers([]);
      return;
    }
    
    try {
      console.log('Загрузка кассиров для заведения:', filters.establishment_id);
      const params = `?establishment_id=${filters.establishment_id}`;
      const response = await cashierAPI.get(`/api/cashier/admin/cashiers/${params}`);
      setCashiers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Ошибка загрузки кассиров:', err);
      setCashiers([]);
    }
  }, [filters.establishment_id]);

  // Загрузка данных
  useEffect(() => {
    loadEstablishments();
  }, [loadEstablishments]);

  useEffect(() => {
    // Загружаем смены только если есть заведения или не требуется фильтр по заведению
    if (establishments.length > 0 || !filters.establishment_id) {
      loadShifts();
    }
  }, [loadShifts, establishments.length]);

  useEffect(() => {
    loadCashiers();
  }, [loadCashiers, filters.establishment_id]);

  const loadShiftDetails = async (shiftId) => {
    try {
      console.log('Загрузка деталей смены:', shiftId);
      const response = await cashierAPI.get(`/api/cashier/admin/shifts/${shiftId}/`);
      setSelectedShift(response.data);
      setShowDetails(true);
    } catch (err) {
      console.error('Ошибка загрузки деталей смены:', err);
      setError(`Ошибка загрузки деталей смены: ${err.message}`);
    }
  };

  const handleFilterChange = (key, value) => {
    console.log('Изменение фильтра:', key, value);
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Сброс на первую страницу при изменении фильтров
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '0 ₽';
    }
    // Округляем до целого числа и форматируем без копеек
    const rounded = Math.round(Number(amount));
    return `${rounded.toLocaleString('ru-RU')} ₽`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Не указано';
    try {
      return new Date(dateString).toLocaleString('ru-RU');
    } catch (error) {
      return 'Неверная дата';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'opened':
        return <FaExclamationCircle className="w-4 h-4 text-yellow-500" />;
      case 'closed':
        return <FaCheckCircle className="w-4 h-4 text-green-500" />;
      case 'suspended':
        return <FaTimes className="w-4 h-4 text-red-500" />;
      default:
        return <FaExclamationCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'opened':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleExportReport = async (shiftId) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Токен авторизации не найден');
        return;
      }

      console.log('Экспорт отчета для смены:', shiftId);
      
      const response = await fetch(`/api/cashier/admin/shifts/${shiftId}/export/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Получаем имя файла из заголовков
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `shift_report_${shiftId}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Создаем blob и скачиваем файл
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Отчет успешно скачан:', filename);
    } catch (error) {
      console.error('Ошибка экспорта отчета:', error);
      setError(`Ошибка экспорта отчета: ${error.message}`);
    }
  };

  if (showDetails && selectedShift) {
    return (
      <ShiftDetailsModal 
        shift={selectedShift} 
        onClose={() => {
          setShowDetails(false);
          setSelectedShift(null);
        }}
      />
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Отображение ошибки */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <FaExclamationCircle className="w-5 h-5 text-red-500 mr-2" />
            <div>
              <h3 className="text-red-800 font-medium">Ошибка загрузки данных</h3>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Статистика */}
      {summaryStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Общие продажи</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(summaryStats.total_sales)}
                </p>
              </div>
              <FaRubleSign className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Количество смен</p>
                <p className="text-2xl font-bold text-gray-900">{summaryStats.shifts_count || 0}</p>
              </div>
              <FaClock className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Всего заказов</p>
                <p className="text-2xl font-bold text-gray-900">{summaryStats.total_orders || 0}</p>
              </div>
              <FaChartLine className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Средний чек</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(summaryStats.avg_sales_per_shift)}
                </p>
              </div>
              <FaRubleSign className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>
      )}

      {/* Фильтры */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Заведение
            </label>
            <select
              value={filters.establishment_id}
              onChange={(e) => handleFilterChange('establishment_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Все заведения</option>
              {establishments.map(est => (
                <option key={est.id} value={est.id}>{est.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Статус
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Все статусы</option>
              <option value="opened">Открытые</option>
              <option value="closed">Закрытые</option>
              <option value="suspended">Приостановленные</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Кассир
            </label>
            <select
              value={filters.cashier_id}
              onChange={(e) => handleFilterChange('cashier_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!filters.establishment_id}
            >
              <option value="">
                {filters.establishment_id ? 'Все кассиры' : 'Выберите заведение'}
              </option>
              {cashiers.map(cashier => (
                <option key={cashier.id} value={cashier.id}>
                  {cashier.name} ({cashier.employee_id})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Дата от
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Дата до
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({
                establishment_id: '',
                status: 'closed',
                date_from: '',
                date_to: '',
                cashier_id: '',
                page: 1,
                limit: 20
              })}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 mr-2"
            >
              Сбросить
            </button>
          </div>

          <div className="flex items-end">
            <button
              onClick={loadShifts}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <FaSync className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Обновить
            </button>
          </div>
        </div>
      </div>

      {/* Таблица смен */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <FaSync className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Загрузка смен...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12 text-red-600">
            <FaExclamationCircle className="w-8 h-8 mr-2" />
            <div className="text-center">
              <p className="font-medium">Ошибка загрузки данных</p>
              <p className="text-sm mt-1">Проверьте подключение к серверу</p>
              <button
                onClick={loadShifts}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Попробовать снова
              </button>
            </div>
          </div>
        ) : shifts.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <FaClock className="w-8 h-8 mr-2" />
            <div className="text-center">
              <p className="font-medium">Смены не найдены</p>
              <p className="text-sm mt-1">Попробуйте изменить фильтры поиска</p>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Смена
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Кассир
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Заведение
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Период
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Продажи
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Заказы
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Статус
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Действия
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {shifts.map((shift) => (
                    <tr key={shift.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {shift.shift_number || 'Не указан'}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {shift.id ? shift.id.slice(0, 8) + '...' : 'Не указан'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FaUser className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {shift.cashier?.name || 'Не указан'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {shift.cashier?.employee_id || 'Не указан'} • {shift.cashier?.role || 'Не указана'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FaMapMarkerAlt className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {shift.establishment?.name || 'Не указано'}
                            </div>
                            {shift.location && (
                              <div className="text-sm text-gray-500">
                                {shift.location.name}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDateTime(shift.opened_at)}
                        </div>
                        {shift.closed_at && (
                          <div className="text-sm text-gray-500">
                            до {formatDateTime(shift.closed_at)}
                          </div>
                        )}
                        {shift.duration && (
                          <div className="text-sm text-blue-600">
                            {shift.duration}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(shift.financial_data?.total_sales)}
                        </div>
                        <div className="text-sm text-gray-500">
                          Нал: {formatCurrency(shift.financial_data?.total_cash_sales)}
                        </div>
                        <div className="text-sm text-gray-500">
                          Карта: {formatCurrency(shift.financial_data?.total_card_sales)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {shift.financial_data?.orders_count || 0}
                        </div>
                        <div className="text-sm text-gray-500">
                          {shift.transactions_stats?.total_transactions || 0} операций
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(shift.status)}`}>
                          {getStatusIcon(shift.status)}
                          <span className="ml-1">{shift.status_display || shift.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => loadShiftDetails(shift.id)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                          title="Просмотр деталей"
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleExportReport(shift.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Экспорт отчета"
                        >
                          <FaDownload className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Пагинация */}
            {pagination && pagination.total_pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.has_prev}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Предыдущая
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.has_next}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Следующая
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Показано <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> до{' '}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total_count)}
                      </span>{' '}
                      из <span className="font-medium">{pagination.total_count}</span> результатов
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={!pagination.has_prev}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <FaChevronLeft className="h-5 w-5" />
                      </button>
                      
                      {/* Номера страниц */}
                      {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pageNum === pagination.page
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={!pagination.has_next}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <FaChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Компонент детального просмотра смены
const ShiftDetailsModal = ({ shift, onClose }) => {
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '0 ₽';
    }
    // Округляем до целого числа и форматируем без копеек
    const rounded = Math.round(Number(amount));
    return `${rounded.toLocaleString('ru-RU')} ₽`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Не указано';
    try {
      return new Date(dateString).toLocaleString('ru-RU');
    } catch (error) {
      return 'Неверная дата';
    }
  };

  const handleExportFromModal = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('Токен авторизации не найден');
        return;
      }

      console.log('Экспорт отчета для смены:', shift.id);
      
      const response = await fetch(`/api/cashier/admin/shifts/${shift.id}/export/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Получаем имя файла из заголовков
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `shift_report_${shift.shift_number || shift.id}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Создаем blob и скачиваем файл
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Отчет успешно скачан:', filename);
    } catch (error) {
      console.error('Ошибка экспорта отчета:', error);
      alert(`Ошибка экспорта отчета: ${error.message}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Заголовок */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Смена {shift.shift_number || 'Не указана'}
            </h2>
            <p className="text-gray-600">
              {shift.cashier?.name || 'Не указан'} • {shift.establishment?.name || 'Не указано'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FaTimes className="w-6 h-6" />
          </button>
        </div>

        {/* Основная информация */}
        <div className="p-6 space-y-6">
          {/* Финансовые данные */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-medium text-green-800 mb-2">Продажи</h3>
              <p className="text-2xl font-bold text-green-900">
                {formatCurrency(shift.financial_data?.total_sales)}
              </p>
              <div className="text-sm text-green-700 mt-2">
                <p>Наличные: {formatCurrency(shift.financial_data?.total_cash_sales)}</p>
                <p>Карта: {formatCurrency(shift.financial_data?.total_card_sales)}</p>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-medium text-blue-800 mb-2">Касса</h3>
              <p className="text-lg font-bold text-blue-900">
                Начало: {formatCurrency(shift.financial_data?.opening_cash)}
              </p>
              {shift.financial_data?.closing_cash !== null && shift.financial_data?.closing_cash !== undefined && (
                <p className="text-lg font-bold text-blue-900">
                  Конец: {formatCurrency(shift.financial_data.closing_cash)}
                </p>
              )}
              {shift.financial_data?.cash_difference !== null && shift.financial_data?.cash_difference !== undefined && (
                <p className={`text-sm mt-2 ${
                  shift.financial_data.cash_difference >= 0 ? 'text-green-700' : 'text-red-700'
                }`}>
                  Разница: {formatCurrency(shift.financial_data.cash_difference)}
                </p>
              )}
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="font-medium text-purple-800 mb-2">Заказы</h3>
              <p className="text-2xl font-bold text-purple-900">
                {shift.financial_data?.orders_count || 0}
              </p>
              <div className="text-sm text-purple-700 mt-2">
                <p>Возвраты: {formatCurrency(shift.financial_data?.total_refunds)}</p>
                <p>Скидки: {formatCurrency(shift.financial_data?.total_discounts)}</p>
              </div>
            </div>
          </div>

          {/* Статистика по часам */}
          {shift.hourly_stats && shift.hourly_stats.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Статистика по часам</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {shift.hourly_stats.map((stat) => (
                    <div key={stat.hour} className="bg-white p-3 rounded border">
                      <div className="font-medium">{stat.hour}:00 - {stat.hour + 1}:00</div>
                      <div className="text-sm text-gray-600">
                        <p>Операций: {stat.transactions_count}</p>
                        <p>Сумма: {formatCurrency(stat.total_amount)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Транзакции */}
          {shift.transactions && shift.transactions.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Транзакции ({shift.transactions.length})
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                <div className="space-y-2">
                  {shift.transactions.map((transaction) => (
                    <div key={transaction.id} className="bg-white p-3 rounded border">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">
                            {transaction.transaction_type_display || transaction.transaction_type}
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatDateTime(transaction.created_at)}
                          </div>
                          {transaction.order && (
                            <div className="text-sm text-blue-600">
                              Заказ: {transaction.order.order_number}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-medium">
                            {formatCurrency(transaction.amount)}
                          </div>
                          {transaction.payment_method_display && (
                            <div className="text-sm text-gray-600">
                              {transaction.payment_method_display}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Заметки */}
          {(shift.notes?.opening_notes || shift.notes?.closing_notes) && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Заметки</h3>
              <div className="space-y-4">
                {shift.notes.opening_notes && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">При открытии</h4>
                    <p className="text-blue-700">{shift.notes.opening_notes}</p>
                  </div>
                )}
                {shift.notes.closing_notes && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-2">При закрытии</h4>
                    <p className="text-green-700">{shift.notes.closing_notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Футер */}
        <div className="flex justify-end gap-3 p-6 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Закрыть
          </button>
          <button
            onClick={handleExportFromModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Экспорт отчета
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashShifts; 